import { IOptionalPagination, IPagedListResponse } from '@edgebox/data-definition-kit';
import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { ClientTaxonomyTermEntity, ITaxonomyTermFilters } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { TaxonomyTermIndexScopeContext } from './TaxonomyTermIndexScope';
import {
  cachedTaxonomyTermsByFacets,
  forgetCachedTaxonomyTerms,
  getCachedTaxonomyTermsByFacets,
  ITaxonomyTermFacetSource,
  joinedTaxonomyTermsByFacets,
  resetCachedTaxonomyData,
} from './useCachedTaxonomyData';
import { HEX_COLOR_PATTERN } from '../../../Components/governanceThemeColors';

/**
 * One fetch of every taxonomy term the viewer can see, keyed for instant lookup.
 *
 * Term keys are referenced everywhere — badge labels, row visibility, chart tints, filter chips —
 * and every reference resolves through this one index rather than its own single-term request.
 *
 * The visible term set is small — a few hundred rows at most — so one paginated list covers all
 * of it. Terms are fetched at every curation status the product displays, which is what lets a
 * lookup answer with a name AND a status without a second request.
 *
 * A screen that declares a `TaxonomyTermIndexScope` gets a bounded variant of the same index: the
 * terms of the facets it names, in one request, instead of the whole catalogue. The whole-catalogue
 * request grows with the project — every crawled site, every cited host, every archived term — and
 * can run up to 65 seconds on a large project, on a screen whose rows could only ever carry terms of
 * a handful of facets. The unscoped fetch stays for screens that have not said which facets they
 * show.
 *
 * The scoped variant keeps no per-facet cache of its own: it reads its facets through
 * `getCachedTaxonomyTermsByFacets`, the same fetch-and-cache the page's own facet lists
 * (`facetConfigs`-style filter dropdowns, breakdown pickers) already use. A facet a screen has
 * already fetched for one of those is never fetched again here — the index just names which facets
 * it needs and asks the one place that already knows how to fetch and cache them.
 */

/**
 * The term fields the index reads.
 *
 * Each is derived from the client entity, so a rename or retype upstream breaks this build rather
 * than silently emptying the index. `taxonomy` is narrowed to the one property actually read: the
 * real entity carries a full taxonomy record with a live reference on it, and demanding all of that
 * would force every test to construct one — which is how a fixture ends up cast into a shape it is
 * not. The real entity still satisfies this, so nothing at a call site has to adapt.
 */
export interface IndexedTaxonomyTerm {
  key: ClientTaxonomyTermEntity['key'];
  name: ClientTaxonomyTermEntity['name'];
  status: ClientTaxonomyTermEntity['status'];
  color?: ClientTaxonomyTermEntity['color'];
  taxonomy?: { facet?: ClientTaxonomyTermEntity['taxonomy']['facet'] };
}

/**
 * The single client call this module makes, as a typed slice of the API surface.
 *
 * Narrow on purpose: a test supplies exactly this and nothing else, with no cast, while the
 * compiler still checks the real `ISyncCoreApi` against it at every call site.
 */
export interface ITaxonomyTermIndexSource {
  service: {
    taxonomyTerms: {
      list: (pagination?: IOptionalPagination, filters?: ITaxonomyTermFilters) => Promise<IPagedListResponse<IndexedTaxonomyTerm>>;
    };
  };
}

/** What the index knows about one term. */
export interface TaxonomyTermIndexEntry {
  name: string;
  status?: TaxonomyTermStatus;
  taxonomyFacet?: TaxonomyFacet;
  /** The term's stored brand colour, when it carries one: a lower-case six-digit hex. */
  color?: string;
}

/** The stored brand colour of a term, normalised, or undefined when it is absent or not a six-digit hex. */
function toStoredColor(color: unknown): string | undefined {
  const value = String(color || '')
    .trim()
    .toLowerCase();
  // Validated at the boundary from the one shared pattern, so a malformed value never reaches a
  // painter that would concatenate an alpha suffix onto it and drop the declaration.
  return HEX_COLOR_PATTERN.test(value) ? value : undefined;
}

export interface TaxonomyTermIndex {
  byKey: ReadonlyMap<string, TaxonomyTermIndexEntry>;
}

/**
 * The statuses the index covers.
 *
 * The four the product ever displays. `PreviousRevision` is deliberately excluded: superseded
 * revisions share their key with the live term, so including them would put two rows under one key
 * — and the endpoint rejects the status outright without the read-all scope, so asking for it would
 * turn every viewer's index fetch into a 403.
 */
export const INDEXED_TERM_STATUSES: readonly TaxonomyTermStatus[] = [
  TaxonomyTermStatus.Active,
  TaxonomyTermStatus.Unapproved,
  TaxonomyTermStatus.Draft,
  TaxonomyTermStatus.Archived,
];

/**
 * Which entry wins when one key comes back at more than one status: the live term first. A key can
 * carry an archived revision beside a draft or unapproved one, and keeping the archived one hides a
 * term the product shows everywhere else.
 */
const STATUS_PREFERENCE: readonly TaxonomyTermStatus[] = [
  TaxonomyTermStatus.Active,
  TaxonomyTermStatus.Unapproved,
  TaxonomyTermStatus.Draft,
  TaxonomyTermStatus.Archived,
];

/** How strongly a status is preferred when one key has several revisions; lower wins. */
export function liveTermStatusRank(status: TaxonomyTermStatus | undefined): number {
  const rank = status ? STATUS_PREFERENCE.indexOf(status) : -1;
  return rank < 0 ? STATUS_PREFERENCE.length : rank;
}

/** Keeps the entry of a key whose status the product would rather show. */
function keepPreferred(byKey: Map<string, TaxonomyTermIndexEntry>, key: string, entry: TaxonomyTermIndexEntry): void {
  const existing = byKey.get(key);
  if (!existing || liveTermStatusRank(entry.status) < liveTermStatusRank(existing.status)) {
    byKey.set(key, entry);
  }
}

/** Rows per request. The endpoint caps a page well below this; pagination covers the rest. */
const INDEX_PAGE_SIZE = 500;

/** Hard stop, so a pathological project cannot turn pagination into its own request storm. */
const INDEX_MAX_PAGES = 20;

type IndexState =
  | { state: 'idle' }
  | { state: 'loading'; promise: Promise<TaxonomyTermIndex> }
  | { state: 'loaded'; index: TaxonomyTermIndex }
  /**
   * A failed index stays failed for the session.
   *
   * Deliberate: the failure this exists to survive is a 429, and re-requesting on the next mount is
   * exactly how the original storm sustained itself. Consumers read `failed` and surface it once; a
   * reload is what retries.
   */
  | { state: 'failed' };

/** The whole-catalogue index, for screens that declare no scope. */
let indexState: IndexState = { state: 'idle' };
/** The merged index per scope, kept so a scope resolves to ONE object and consumers' memos hold. */
const scopedIndexes = new Map<string, TaxonomyTermIndex>();
/**
 * The scopes whose index failed. Like the whole-catalogue index, a failed scope stays failed for the
 * session: the per-facet cache it reads through forgets a failure so a screen can ask again, and the
 * index keeps its own memory so the term-rendering components mounting after it do not. The one way
 * out asks nothing: once every facet of the scope has loaded into that cache since — the screen asked
 * again, and its request carried them — the index is built from the cache.
 */
const failedScopes = new Set<string>();
/**
 * Counts the forgets, so a scoped read that was already on its way when its facets were forgotten
 * answers its caller without caching the list it read before.
 */
let scopedIndexGeneration = 0;

function toKnownFacet(facet: unknown): TaxonomyFacet | undefined {
  return typeof facet === 'string' && (Object.values(TaxonomyFacet) as string[]).includes(facet) ? (facet as TaxonomyFacet) : undefined;
}

/** Folds a list of terms into an index slice, keeping the live entry for a repeated key. */
function indexFromTerms(terms: readonly ClientTaxonomyTermEntity[]): TaxonomyTermIndex {
  const byKey = new Map<string, TaxonomyTermIndexEntry>();
  for (const term of terms) {
    const key = String(term.key || '').trim();
    const name = String(term.name || '').trim();
    // A term with no key cannot be looked up, and one with no name cannot be rendered as anything
    // but its key — which is a defect, so it is left out and reads as missing.
    if (!key || !name) {
      continue;
    }
    keepPreferred(byKey, key, {
      name,
      status: term.status,
      taxonomyFacet: toKnownFacet(term.taxonomy?.facet),
      color: toStoredColor(term.color),
    });
  }
  return { byKey };
}

async function fetchTaxonomyTermIndex(api: ITaxonomyTermIndexSource): Promise<TaxonomyTermIndex> {
  const byKey = new Map<string, TaxonomyTermIndexEntry>();

  for (let page = 0; page < INDEX_MAX_PAGES; page += 1) {
    const response = await api.service.taxonomyTerms.list(
      { page, itemsPerPage: INDEX_PAGE_SIZE },
      { statuses: [...INDEXED_TERM_STATUSES] }
    );

    const items = response.items || [];
    for (const term of items) {
      const key = String(term.key || '').trim();
      const name = String(term.name || '').trim();
      // A term with no key cannot be looked up, and one with no name cannot be rendered as
      // anything but its key — which is a defect, so it is left out and reads as missing.
      if (!key || !name) {
        continue;
      }
      keepPreferred(byKey, key, {
        name,
        status: term.status,
        taxonomyFacet: toKnownFacet(term.taxonomy?.facet),
        color: toStoredColor(term.color),
      });
    }

    if (page + 1 >= (response.numberOfPages ?? 1) || items.length < INDEX_PAGE_SIZE) {
      break;
    }
  }

  return { byKey };
}

/**
 * Runs one index through its state machine: a loaded index is returned, an in-flight one is joined,
 * a failed one stays failed, and an idle one is fetched exactly once.
 */
function resolveIndex(
  current: IndexState,
  remember: (next: IndexState) => void,
  fetchIndex: () => Promise<TaxonomyTermIndex>
): Promise<TaxonomyTermIndex> {
  if (current.state === 'loaded') {
    return Promise.resolve(current.index);
  }

  if (current.state === 'loading') {
    return current.promise;
  }

  if (current.state === 'failed') {
    return Promise.reject(new Error('Taxonomy term index could not be loaded.'));
  }

  const promise = fetchIndex()
    .then((index) => {
      remember({ state: 'loaded', index });
      return index;
    })
    .catch((error) => {
      remember({ state: 'failed' });
      throw error;
    });

  remember({ state: 'loading', promise });
  return promise;
}

/**
 * The whole-catalogue index, fetched once per session and shared by every consumer.
 *
 * Concurrent callers join the one in-flight promise rather than starting their own — that sharing
 * is the whole point, since a page mounts dozens of term-rendering components at once.
 *
 * No `accessScope` filter is passed, and none is needed: the endpoint narrows by the caller's own
 * read scopes, so a single request returns the public terms (engines, prompt purposes) and the
 * customer- and project-scoped ones together, exactly as the viewer may see them.
 */
export function getTaxonomyTermIndex(api: ITaxonomyTermIndexSource): Promise<TaxonomyTermIndex> {
  return resolveIndex(
    indexState,
    (next) => {
      indexState = next;
    },
    () => fetchTaxonomyTermIndex(api)
  );
}

/** A scope's identity as a SET of facets: neither order nor repetition makes it a different scope. */
export function taxonomyTermIndexScopeKey(facets: readonly TaxonomyFacet[]): string {
  return Array.from(new Set(facets)).sort().join(',');
}

/** Keeps the index built from a failed scope's facets once they have all loaded, and clears the failure. */
function rememberRecoveredScope(
  scopeKey: string,
  termsByFacet: ReadonlyMap<TaxonomyFacet, readonly ClientTaxonomyTermEntity[]>
): TaxonomyTermIndex {
  failedScopes.delete(scopeKey);
  const alreadyMerged = scopedIndexes.get(scopeKey);
  if (alreadyMerged) {
    return alreadyMerged;
  }
  const scoped = indexFromTerms(Array.from(termsByFacet.values()).flat());
  scopedIndexes.set(scopeKey, scoped);
  return scoped;
}

/**
 * The index of a scope that failed, built from the per-facet cache once every facet of the scope has
 * loaded there since; undefined while any is missing. Asks nothing, so it cannot start a storm.
 */
function recoveredScopeIndex(scopeKey: string, facets: readonly TaxonomyFacet[]): TaxonomyTermIndex | undefined {
  const termsByFacet = cachedTaxonomyTermsByFacets(facets);
  return termsByFacet ? rememberRecoveredScope(scopeKey, termsByFacet) : undefined;
}

/**
 * The index over the given facets only, read through the established per-facet cache
 * (`getCachedTaxonomyTermsByFacets`) rather than a fetch of its own, and merged into one lookup.
 *
 * That cache answers each facet from its cached list or a request already on its way, and asks for
 * the rest in one request — so a facet another part of the page already fetched (a filter dropdown,
 * `facetConfigs`, the scope itself) is reused here instead of fetched a second time. It forgets a
 * failure; the scope's own failure is remembered in `failedScopes`.
 *
 * A key of any other facet resolves as missing — precisely what the whole index answers for a
 * deleted term — so a screen has to name every facet its rows can carry.
 */
export function getScopedTaxonomyTermIndex(api: ITaxonomyTermFacetSource, facets: readonly TaxonomyFacet[]): Promise<TaxonomyTermIndex> {
  const scopeKey = taxonomyTermIndexScopeKey(facets);
  const merged = scopedIndexes.get(scopeKey);
  if (merged) {
    return Promise.resolve(merged);
  }
  if (failedScopes.has(scopeKey)) {
    // Recovers from the cache, or from a refill already on its way — never from a request of its own.
    const refill = joinedTaxonomyTermsByFacets(facets);
    return refill
      ? refill.then((termsByFacet) => rememberRecoveredScope(scopeKey, termsByFacet))
      : Promise.reject(new Error('Taxonomy term index could not be loaded.'));
  }

  const generation = scopedIndexGeneration;
  const request = getCachedTaxonomyTermsByFacets(api, facets).then((termsByFacet) => {
    // Two callers can be in flight for the same scope; the first to finish decides the object.
    const alreadyMerged = scopedIndexes.get(scopeKey);
    if (alreadyMerged) {
      return alreadyMerged;
    }

    const scoped = indexFromTerms(Array.from(termsByFacet.values()).flat());
    if (generation === scopedIndexGeneration) {
      scopedIndexes.set(scopeKey, scoped);
    }
    return scoped;
  });

  return request.catch((error: unknown) => {
    failedScopes.add(scopeKey);
    throw error;
  });
}

/**
 * Forgets what the frame knows of the given facets' tags: every scoped index holding one of them and
 * the facets' cached tags. A screen that writes a tag calls it, so a screen rendering that facet next
 * asks for its tags again instead of rendering a new one by its key. The whole-catalogue index is
 * left as it is: its read is the heaviest the frame makes, and a failed one stays failed.
 */
export function forgetTaxonomyTermIndexFacets(facets: readonly TaxonomyFacet[]): void {
  scopedIndexGeneration += 1;
  const forgotten = new Set<string>(facets);
  const holdsForgottenFacet = (scopeKey: string) => scopeKey.split(',').some((facet) => forgotten.has(facet));
  for (const scopeKey of Array.from(scopedIndexes.keys())) {
    if (holdsForgottenFacet(scopeKey)) {
      scopedIndexes.delete(scopeKey);
    }
  }
  for (const scopeKey of Array.from(failedScopes)) {
    if (holdsForgottenFacet(scopeKey)) {
      failedScopes.delete(scopeKey);
    }
  }
  forgetCachedTaxonomyTerms(facets);
}

/**
 * Drops every cached index. Exported for tests, which need each case to start from nothing.
 *
 * Also drops the established per-facet cache the scoped index now reads through: a facet fetched by
 * one test case must not answer a later one that expects to fetch it again.
 */
export function resetTaxonomyTermIndex(): void {
  indexState = { state: 'idle' };
  scopedIndexes.clear();
  failedScopes.clear();
  resetCachedTaxonomyData();
}

export interface TaxonomyTermIndexState {
  /** Present once the index has loaded. */
  index?: TaxonomyTermIndex;
  loading: boolean;
  /** True when the index could not be loaded at all — the page says so once and stops asking. */
  failed: boolean;
}

/** What is already known about the index a scope (or the absence of one) asks for, without fetching. */
function settledIndexState(scope: readonly TaxonomyFacet[] | undefined): TaxonomyTermIndexState {
  if (!scope) {
    return {
      index: indexState.state === 'loaded' ? indexState.index : undefined,
      loading: false,
      failed: indexState.state === 'failed',
    };
  }

  const scopeKey = taxonomyTermIndexScopeKey(scope);
  // A failed scope whose facets have all loaded since is no longer failed.
  const recovered = failedScopes.has(scopeKey) ? recoveredScopeIndex(scopeKey, scope) : undefined;
  return {
    index: recovered ?? scopedIndexes.get(scopeKey),
    loading: false,
    // A merged or a failed scope decides this; any other scope starts unresolved.
    failed: failedScopes.has(scopeKey),
  };
}

/**
 * The index as React state.
 *
 * Every term-rendering hook reads this, so a page that references two hundred keys still issues the
 * one index fetch its first component triggered — or, inside a `TaxonomyTermIndexScope`, the one
 * request for the declared facets that the scope itself already sent.
 */
export function useTaxonomyTermIndex(api: ITaxonomyTermFacetSource | undefined): TaxonomyTermIndexState {
  const scope = React.useContext(TaxonomyTermIndexScopeContext);
  // The scope is a set of facets, so the effect is keyed on that set rather than on the array's
  // identity: a caller re-rendering with an equal array must not re-resolve the index.
  const scopeKey = scope ? taxonomyTermIndexScopeKey(scope) : undefined;
  const [state, setState] = React.useState<TaxonomyTermIndexState>(() => {
    const settled = settledIndexState(scope);
    // A failed scope may be refilling; the effect finds out once its pass has run.
    return scope && settled.failed ? { index: undefined, loading: true, failed: false } : settled;
  });

  React.useEffect(() => {
    if (!api) {
      setState({ index: undefined, loading: false, failed: false });
      return;
    }

    const settled = settledIndexState(scope);
    if (settled.index || (settled.failed && !scope)) {
      setState(settled);
      return;
    }

    let active = true;
    if (settled.failed && scope) {
      // A failed scope whose facets a request is refilling — the screen asked again, or the scope
      // mounted again and asked for them — recovers when that request answers, and reads as loading
      // until then rather than showing keys; with no refill on its way it stays failed. Looked for
      // once the effects of this pass have run, so a refill the scope starts in the same pass is
      // found; the index asks nothing itself.
      setState({ index: undefined, loading: true, failed: false });
      void Promise.resolve()
        .then(() => getScopedTaxonomyTermIndex(api, scope))
        .then(
          (index) => {
            if (active) {
              setState({ index, loading: false, failed: false });
            }
          },
          () => {
            if (active) {
              setState({ index: undefined, loading: false, failed: true });
            }
          }
        );
      return () => {
        active = false;
      };
    }

    setState({ index: undefined, loading: true, failed: false });

    void (scope ? getScopedTaxonomyTermIndex(api, scope) : getTaxonomyTermIndex(api))
      .then((index) => {
        if (active) {
          setState({ index, loading: false, failed: false });
        }
      })
      .catch(() => {
        if (active) {
          setState({ index: undefined, loading: false, failed: true });
        }
      });

    return () => {
      active = false;
    };
    // `scope` is read, but `scopeKey` is what identifies it — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, scopeKey]);

  return state;
}
